import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RefreshCw } from 'lucide-react';

interface SimpleCaptchaProps {
  onVerify?: (isValid: boolean) => void;
  onValidationChange?: (isValid: boolean) => void;
  onReset?: () => void;
  disabled?: boolean;
}

export interface SimpleCaptchaRef {
  validate: () => boolean;
  refresh: () => void;
}

// Enhanced security patterns to prevent automated solving
const CAPTCHA_PATTERNS = [
  // Mathematical operations
  () => {
    const a = Math.floor(Math.random() * 20) + 1;
    const b = Math.floor(Math.random() * 20) + 1;
    const operations = ['+', '-', '*'];
    const op = operations[Math.floor(Math.random() * operations.length)];
    let result: number;
    let question: string;
    
    switch (op) {
      case '+':
        result = a + b;
        question = `${a} + ${b}`;
        break;
      case '-':
        // Ensure positive result
        const larger = Math.max(a, b);
        const smaller = Math.min(a, b);
        result = larger - smaller;
        question = `${larger} - ${smaller}`;
        break;
      case '*':
        const smallA = Math.floor(Math.random() * 10) + 1;
        const smallB = Math.floor(Math.random() * 10) + 1;
        result = smallA * smallB;
        question = `${smallA} × ${smallB}`;
        break;
      default:
        result = a + b;
        question = `${a} + ${b}`;
    }
    
    return { question: `What is ${question}?`, answer: result.toString() };
  },
  
  // Text-based questions
  () => {
    const questions = [
      { q: "What color do you get when you mix red and blue?", a: "purple" },
      { q: "How many days are in a week?", a: "7" },
      { q: "What is the opposite of 'hot'?", a: "cold" },
      { q: "What animal says 'moo'?", a: "cow" },
      { q: "What comes after Monday?", a: "tuesday" },
      { q: "How many wheels does a bicycle have?", a: "2" },
      { q: "What is 10 divided by 2?", a: "5" },
      { q: "What is the first letter of the alphabet?", a: "a" },
    ];
    
    const selected = questions[Math.floor(Math.random() * questions.length)];
    return { question: selected.q, answer: selected.a };
  },
  
  // Pattern recognition
  () => {
    const patterns = [
      { q: "Complete the sequence: 2, 4, 6, 8, ?", a: "10" },
      { q: "Complete the sequence: 1, 3, 5, 7, ?", a: "9" },
      { q: "Complete the sequence: 5, 10, 15, 20, ?", a: "25" },
      { q: "What comes next: A, B, C, D, ?", a: "e" },
    ];
    
    const selected = patterns[Math.floor(Math.random() * patterns.length)];
    return { question: selected.q, answer: selected.a };
  }
];

const SimpleCaptcha = forwardRef<SimpleCaptchaRef, SimpleCaptchaProps>(({ 
  onVerify, 
  onValidationChange,
  onReset, 
  disabled = false 
}, ref) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [userInput, setUserInput] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isValid, setIsValid] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const MAX_ATTEMPTS = 3;
  const LOCKOUT_DURATION = 300; // 5 minutes in seconds

  const generateCaptcha = () => {
    const patternGenerator = CAPTCHA_PATTERNS[Math.floor(Math.random() * CAPTCHA_PATTERNS.length)];
    const { question, answer } = patternGenerator();
    setQuestion(question);
    setAnswer(answer.toLowerCase());
    setUserInput('');
    setIsValid(false);
    setShowSuccess(false);
    onValidationChange?.(false);
    
    // Log security event
    console.log('[CAPTCHA] New challenge generated');
  };

  const handleLockout = () => {
    setIsLocked(true);
    setTimeRemaining(LOCKOUT_DURATION);
    setIsValid(false);
    setShowSuccess(false);
    onValidationChange?.(false);
    console.log('[CAPTCHA_SECURITY] Account locked due to multiple failed attempts');
    
    const countdown = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(countdown);
          setIsLocked(false);
          setAttempts(0);
          generateCaptcha();
          console.log('[CAPTCHA] Lockout period ended, generating new challenge');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const validateInput = (input: string): boolean => {
    const normalizedInput = input.trim().toLowerCase();
    const normalizedAnswer = answer.trim().toLowerCase();
    return normalizedInput === normalizedAnswer;
  };

  const handleVerify = () => {
    if (isLocked || disabled || isValid) return;

    const inputIsValid = validateInput(userInput);
    
    if (inputIsValid) {
      console.log('[CAPTCHA] Verification successful');
      setIsValid(true);
      setShowSuccess(true);
      onVerify?.(true);
      onValidationChange?.(true);
      setAttempts(0);
      // Don't generate new challenge immediately - keep the valid state
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setIsValid(false);
      setShowSuccess(false);
      
      console.log(`[CAPTCHA_SECURITY] Failed attempt ${newAttempts}/${MAX_ATTEMPTS}`);
      
      if (newAttempts >= MAX_ATTEMPTS) {
        handleLockout();
        onVerify?.(false);
        onValidationChange?.(false);
      } else {
        onVerify?.(false);
        onValidationChange?.(false);
        generateCaptcha(); // Generate new challenge after failed attempt
      }
    }
  };

  const handleReset = () => {
    if (!isLocked && !disabled) {
      generateCaptcha();
      setAttempts(0);
      setIsValid(false);
      setShowSuccess(false);
      onValidationChange?.(false);
      onReset?.();
      console.log('[CAPTCHA] Manual reset triggered');
    }
  };

  // Expose methods through ref
  useImperativeHandle(ref, () => ({
    validate: () => {
      return isValid;
    },
    refresh: () => {
      handleReset();
    }
  }));

  useEffect(() => {
    generateCaptcha();
  }, []);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (isLocked) {
    return (
      <div className="space-y-4 p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="text-center">
          <p className="text-red-600 font-medium">Too many failed attempts</p>
          <p className="text-red-500 text-sm">Please wait {formatTime(timeRemaining)} before trying again</p>
        </div>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="space-y-4 p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="text-center">
          <p className="text-green-600 font-medium">✓ Security verification passed</p>
          <p className="text-green-500 text-sm">You can now proceed with login</p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleReset}
          className="w-full"
          title="Get new question"
          disabled={disabled}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          New Challenge
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Security Verification {attempts > 0 && `(${attempts}/${MAX_ATTEMPTS} attempts)`}
        </label>
        <p className="text-gray-600 font-medium">{question}</p>
        <div className="flex gap-2">
          <Input
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            placeholder="Enter your answer"
            className="flex-1"
            onKeyPress={(e) => e.key === 'Enter' && handleVerify()}
            maxLength={20}
            disabled={disabled || isLocked}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleReset}
            className="px-3"
            title="Get new question"
            disabled={disabled || isLocked}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        <Button
          type="button"
          onClick={handleVerify}
          disabled={!userInput.trim() || disabled || isLocked}
          className="w-full"
          size="sm"
        >
          Verify
        </Button>
        {attempts > 0 && (
          <p className="text-red-500 text-xs text-center">
            Incorrect answer. {MAX_ATTEMPTS - attempts} attempts remaining.
          </p>
        )}
      </div>
    </div>
  );
});

SimpleCaptcha.displayName = 'SimpleCaptcha';

export default SimpleCaptcha;
